import React, { useMemo } from 'react';
import { BarChart, TrendingUp, Users, BookOpen, Award, Calendar } from 'lucide-react';

const StatistiquesEvaluations = ({ controles }) => {
  // Calcul des statistiques
  const stats = useMemo(() => {
    if (!controles || controles.length === 0) {
      return {
        totalControles: 0,
        controlesNotes: 0,
        controlesEnAttente: 0,
        classesActives: [],
        modulesActifs: [],
        repartitionTypes: {},
        tendanceHebdomadaire: [],
        topClasses: [],
        topModules: []
      };
    }

    // Statistiques de base
    const totalControles = controles.length;
    const controlesNotes = controles.filter(c => c.status_note === true).length;
    const controlesEnAttente = totalControles - controlesNotes;

    // Classes actives
    const classesMap = new Map();
    controles.forEach(c => {
      if (c.classe) {
        const key = c.classe.id;
        if (!classesMap.has(key)) {
          classesMap.set(key, {
            id: c.classe.id,
            libelle: c.classe.libelle,
            count: 0
          });
        }
        classesMap.get(key).count++;
      }
    });
    const classesActives = Array.from(classesMap.values()).sort((a, b) => b.count - a.count);

    // Modules actifs
    const modulesMap = new Map();
    controles.forEach(c => {
      if (c.module) {
        const key = c.module.id;
        if (!modulesMap.has(key)) {
          modulesMap.set(key, {
            id: c.module.id,
            libelle: c.module.libelle || c.module.module?.libelle || 'Module sans nom',
            count: 0
          });
        }
        modulesMap.get(key).count++;
      }
    });
    const modulesActifs = Array.from(modulesMap.values()).sort((a, b) => b.count - a.count);

    // Répartition par types
    const repartitionTypes = controles.reduce((acc, c) => {
      const type = c.libelle || 'Non défini';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    // Tendance récente (affichage des vraies dates des contrôles)
    let tendanceHebdomadaire = [];
    
    if (controles.length > 0) {
      // Grouper les contrôles par date
      const controlesParDate = controles.reduce((acc, controle) => {
        const date = controle.date_controle;
        if (!acc[date]) {
          acc[date] = 0;
        }
        acc[date]++;
        return acc;
      }, {});
      
      // Prendre les 7 dernières dates uniques (ou toutes s'il y en a moins)
      const datesUniques = Object.keys(controlesParDate).sort();
      const derniersDates = datesUniques.slice(-7);
      
      tendanceHebdomadaire = derniersDates.map(dateStr => {
        try {
          const date = new Date(dateStr);
          return {
            date: dateStr,
            jour: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
            count: controlesParDate[dateStr]
          };
        } catch (error) {
          return {
            date: dateStr,
            jour: dateStr.slice(-2) + '/' + dateStr.slice(5, 7), // Format dd/mm en fallback
            count: controlesParDate[dateStr]
          };
        }
      });
    }

    return {
      totalControles,
      controlesNotes,
      controlesEnAttente,
      classesActives,
      modulesActifs,
      repartitionTypes,
      tendanceHebdomadaire,
      topClasses: classesActives.slice(0, 3),
      topModules: modulesActifs.slice(0, 3)
    };
  }, [controles]);

  return (
    <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#202654] to-[#202654]/80 px-6 py-4">
        <div className="flex items-center">
          <BarChart className="h-6 w-6 text-white mr-3" />
          <h2 className="text-xl font-semibold text-white">Statistiques détaillées</h2>
        </div>
      </div>

      <div className="p-6">
        {/* Métriques principales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium mb-1">Total contrôles</p>
                <p className="text-3xl font-bold text-blue-800">{stats.totalControles}</p>
                <p className="text-xs text-blue-500 mt-1">Toutes évaluations</p>
              </div>
              <div className="bg-blue-200 p-3 rounded-full">
                <BookOpen className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium mb-1">Notés</p>
                <p className="text-3xl font-bold text-green-800">{stats.controlesNotes}</p>
                <p className="text-xs text-green-500 mt-1">
                  {stats.totalControles > 0 ? Math.round((stats.controlesNotes / stats.totalControles) * 100) : 0}% du total
                </p>
              </div>
              <div className="bg-green-200 p-3 rounded-full">
                <Award className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium mb-1">En attente</p>
                <p className="text-3xl font-bold text-orange-800">{stats.controlesEnAttente}</p>
                <p className="text-xs text-orange-500 mt-1">
                  {stats.totalControles > 0 ? Math.round((stats.controlesEnAttente / stats.totalControles) * 100) : 0}% du total
                </p>
              </div>
              <div className="bg-orange-200 p-3 rounded-full">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium mb-1">Classes actives</p>
                <p className="text-3xl font-bold text-purple-800">{stats.classesActives.length}</p>
                <p className="text-xs text-purple-500 mt-1">
                  {stats.modulesActifs.length} modules
                </p>
              </div>
              <div className="bg-purple-200 p-3 rounded-full">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Graphiques et tableaux */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Tendance hebdomadaire */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-[#202654]" />
              Contrôles récents
            </h3>
            <div className="space-y-3">
              {stats.tendanceHebdomadaire.length > 0 ? (
                stats.tendanceHebdomadaire.map((jour, index) => {
                  const maxCount = Math.max(...stats.tendanceHebdomadaire.map(j => j.count), 1);
                  const percentage = maxCount > 0 ? (jour.count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 w-16">
                        {jour.jour}
                      </span>
                      <span className="text-xs text-gray-400 w-20">
                        {new Date(jour.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                      </span>
                      <div className="flex-1 mx-3">
                        <div className="bg-gray-200 rounded-full h-3 shadow-inner">
                          <div 
                            className="bg-gradient-to-r from-[#202654] to-[#202654]/80 h-3 rounded-full transition-all duration-700 shadow-sm"
                            style={{ 
                              width: `${Math.max(percentage, jour.count > 0 ? 8 : 0)}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-gray-900 w-8 text-right">
                        {jour.count}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune évaluation cette semaine</p>
                </div>
              )}
            </div>
          </div>

          {/* Répartition par types */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart className="h-5 w-5 mr-2 text-[#202654]" />
              Répartition par types
            </h3>
            <div className="space-y-3">
              {Object.entries(stats.repartitionTypes).length > 0 ? (
                Object.entries(stats.repartitionTypes).map(([type, count], index) => {
                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500'];
                  const bgColors = ['bg-blue-50', 'bg-green-50', 'bg-purple-50', 'bg-orange-50'];
                  return (
                    <div key={type} className={`p-3 ${bgColors[index % bgColors.length]} rounded-lg border border-gray-100`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{type}</span>
                        <span className="text-lg font-bold text-gray-900">{count}</span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-2 shadow-inner">
                        <div 
                          className={`${colors[index % colors.length]} h-2 rounded-full transition-all duration-700 shadow-sm`}
                          style={{ 
                            width: `${stats.totalControles > 0 ? (count / stats.totalControles) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {stats.totalControles > 0 ? Math.round((count / stats.totalControles) * 100) : 0}% du total
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BarChart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun type d'évaluation disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top classes et modules */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Top classes */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2 text-[#202654]" />
              Classes les plus actives
            </h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Classe
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Contrôles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.topClasses.slice(0, 5).map((classe, index) => (
                    <tr key={classe.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {classe.libelle}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#202654] text-white">
                          {classe.count}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {stats.topClasses.length === 0 && (
                    <tr>
                      <td colSpan="2" className="px-4 py-8 text-center text-gray-500">
                        Aucune donnée disponible
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top modules */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-[#202654]" />
              Modules les plus évalués
            </h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Module
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Contrôles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.topModules.slice(0, 5).map((module, index) => (
                    <tr key={module.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {module.libelle}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#202654] text-white">
                          {module.count}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {stats.topModules.length === 0 && (
                    <tr>
                      <td colSpan="2" className="px-4 py-8 text-center text-gray-500">
                        Aucune donnée disponible
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatistiquesEvaluations; 